import type { Styles } from '@adornis/ass/style.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export type BreadCrumbArray = Array<{
  content: Renderable | string;
  icon?: string;
  href?: string;
  active: boolean;
}>;

@customElement('bh-breadcrumb')
export class BHBreadcrumb extends BuchungsheldLitElement {
  @property({ attribute: false }) items?: BreadCrumbArray;
  @property() additionalAction?: Renderable;

  @property({ attribute: 'hide-up-button', type: Boolean }) hideUpButton?: boolean;
  @property({ attribute: false }) goUp?: () => void;

  override render() {
    return html`
      <x-flex w100 horizontal crossaxis-center space="md" ${css({ minHeight: '65px' })} space-between>
        <x-flex horizontal crossaxis-center space="xs">
          ${this.hideUpButton
            ? nothing
            : html` <tr-button mode="icon" icon="arrow_upward" @click=${() => this._goUp()}></tr-button> `}
          ${this.items?.map((item, index) => {
            const classes: string[] = [];
            item.href && classes.push('href');
            item.active && classes.push('active');
            return html`
              <x-flex class=${classes.join(' ')} @click=${() => item.href && goTo(item.href)} center crossaxis-center>
                ${typeof item.content === 'string' ? html`<x-text>${item.content}</x-text>` : item.content}
              </x-flex>
              ${(this.items ?? []).length > index + 1 ? html`<x-icon> arrow_right </x-icon>` : nothing}
            `;
          })}
        </x-flex>
        ${this.additionalAction ? this.additionalAction : nothing}
      </x-flex>
    `;
  }

  private _goUp() {
    if (this.goUp) return this.goUp();

    const newPath = routingState.path.split('/').slice(0, -1);
    if (!newPath.length) return;
    goTo(newPath.join('/'));
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          zIndex: '9999',
          position: 'sticky',
          top: '0',
          flex: '1',
          minHeight: '65px',
          background: this.colors.tone.snowWhite,
        },
        '.breadcrumb': {
          color: '#252525',
          borderRadius: this.sizes.borderRadius,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          boxShadow: '0 2px 5px rgba(0,0,0,0.26)',
          padding: `${this.spacing.xs} ${this.spacing.md}`,
          userSelect: 'none',
        },
        '.active': {
          color: this.colors.secondary.shade(400),
          fontWeight: '400',
        },
        '.href': {
          cursor: 'pointer',
          color: this.colors.accent.shade(200),
        },
      },
    ] as Styles[];
  }
}
